import { useMemo, useState, type DragEvent } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Mail, MessageCircle, MoreHorizontal, Pencil, Phone, Plus, Star, StarOff, Trash2, X } from "lucide-react";

export type ContactManagerItem = {
  name?: string | null;
  position?: string | null;
  email?: string | null;
  telephone?: string | null;
  is_primary?: boolean | null;
  no_response?: boolean | null;
};

export type ContactManagerChangeReason =
  | "added"
  | "updated"
  | "removed"
  | "priority_updated"
  | "priority_removed"
  | "no_response_updated";

type MissingInfoPayload = {
  title: string;
  description: string;
};

type ContactManagerPanelProps = {
  contacts: ContactManagerItem[];
  disabled?: boolean;
  onChange: (nextContacts: ContactManagerItem[], reason: ContactManagerChangeReason) => void;
  onSendEmail?: (contact: ContactManagerItem) => void;
  onSendWhatsApp?: (contact: ContactManagerItem) => void;
  onSendCall?: (contact: ContactManagerItem) => void;
  onMissingInfo?: (payload: MissingInfoPayload) => void;
};

const contactMenuContentClassName = "w-56 p-2";
const contactMenuItemClassName = "gap-2.5 rounded-md px-3 py-2";
const contactMenuSeparatorClassName = "my-1.5";

const normalizeContact = (contact: ContactManagerItem): ContactManagerItem => ({
  ...contact,
  is_primary: Boolean(contact.is_primary),
  no_response: Boolean(contact.no_response ?? (contact as { noResponse?: boolean | null }).noResponse),
});

const enforceSinglePriority = (items: ContactManagerItem[]): ContactManagerItem[] => {
  let foundPrimary = false;
  return items.map((contact) => {
    if (contact.is_primary && !foundPrimary) {
      foundPrimary = true;
      return contact;
    }
    if (contact.is_primary && foundPrimary) {
      return { ...contact, is_primary: false };
    }
    return contact;
  });
};

const parsePhoneOptions = (telephone?: string | null): string[] => {
  const source = telephone?.trim();
  if (!source) return [];

  const normalized = source
    .replace(/\s+(e|and|ou)\s+/gi, ";")
    .replace(/[\n\r]+/g, ";")
    .replace(/[|/,]+/g, ";");

  const chunks = normalized
    .split(";")
    .map((item) => item.trim())
    .filter(Boolean);

  const values = chunks.length > 0 ? chunks : [source];
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const value of values) {
    const key = value.replace(/\D/g, "") || value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(value);
  }
  return unique;
};

const trimValue = (value?: string | null) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

const ContactManagerPanel = ({
  contacts,
  disabled = false,
  onChange,
  onSendEmail,
  onSendWhatsApp,
  onSendCall,
  onMissingInfo,
}: ContactManagerPanelProps) => {
  const [showAllContacts, setShowAllContacts] = useState(false);
  const [addingContact, setAddingContact] = useState(false);
  const [editingContactIndex, setEditingContactIndex] = useState<number | null>(null);
  const [newContact, setNewContact] = useState<ContactManagerItem>({
    name: "",
    position: "",
    email: "",
    telephone: "",
  });

  const normalizedContacts = useMemo(
    () => enforceSinglePriority((contacts ?? []).map((contact) => normalizeContact(contact))),
    [contacts],
  );

  const priorityContactIndex = useMemo(
    () => normalizedContacts.findIndex((contact) => Boolean(contact.is_primary)),
    [normalizedContacts],
  );

  const priorityContact = useMemo(
    () => (priorityContactIndex >= 0 ? normalizedContacts[priorityContactIndex] : null),
    [normalizedContacts, priorityContactIndex],
  );

  const otherContacts = useMemo(
    () =>
      normalizedContacts
        .map((contact, index) => ({ contact, index }))
        .filter((entry) => !entry.contact.is_primary),
    [normalizedContacts],
  );

  const visibleOtherContacts = useMemo(
    () => (showAllContacts ? otherContacts : otherContacts.slice(0, 5)),
    [otherContacts, showAllContacts],
  );

  const notifyMissingInfo = (payload: MissingInfoPayload) => {
    onMissingInfo?.(payload);
  };

  const emitChange = (nextContacts: ContactManagerItem[], reason: ContactManagerChangeReason) => {
    onChange(enforceSinglePriority(nextContacts.map((contact) => normalizeContact(contact))), reason);
  };

  const resetContactForm = () => {
    setAddingContact(false);
    setEditingContactIndex(null);
    setNewContact({ name: "", position: "", email: "", telephone: "" });
  };

  const startAddContact = () => {
    setEditingContactIndex(null);
    setNewContact({ name: "", position: "", email: "", telephone: "" });
    setAddingContact(true);
  };

  const startEditContact = (index: number) => {
    const target = normalizedContacts[index];
    if (!target) return;
    setEditingContactIndex(index);
    setNewContact({
      name: target.name ?? "",
      position: target.position ?? "",
      email: target.email ?? "",
      telephone: target.telephone ?? "",
    });
    setAddingContact(true);
  };

  const saveContact = () => {
    const candidateName = trimValue(newContact.name);
    const candidatePosition = trimValue(newContact.position);
    const candidateEmail = trimValue(newContact.email);
    const candidateTelephone = trimValue(newContact.telephone);

    if (!candidateName && !candidateEmail && !candidateTelephone) {
      notifyMissingInfo({
        title: "Missing contact info",
        description: "Add at least a name, email, or phone.",
      });
      return;
    }

    if (editingContactIndex !== null) {
      const existing = normalizedContacts[editingContactIndex];
      if (!existing) return;
      const updated: ContactManagerItem = {
        ...existing,
        name: candidateName,
        position: candidatePosition,
        email: candidateEmail,
        telephone: candidateTelephone,
      };
      emitChange(
        normalizedContacts.map((contact, index) => (index === editingContactIndex ? updated : contact)),
        "updated",
      );
      resetContactForm();
      return;
    }

    const shouldBePrimary = normalizedContacts.length === 0;
    emitChange(
      [
        ...normalizedContacts,
        {
          name: candidateName,
          position: candidatePosition,
          email: candidateEmail,
          telephone: candidateTelephone,
          is_primary: shouldBePrimary,
          no_response: false,
        },
      ],
      "added",
    );
    resetContactForm();
  };

  const setPriorityContact = (index: number | null) => {
    emitChange(
      normalizedContacts.map((contact, currentIndex) => ({
        ...contact,
        is_primary: index !== null ? currentIndex === index : false,
      })),
      index === null ? "priority_removed" : "priority_updated",
    );
  };

  const updateContactNoResponse = (index: number, value: boolean) => {
    emitChange(
      normalizedContacts.map((contact, currentIndex) =>
        currentIndex === index ? { ...contact, no_response: value } : contact,
      ),
      "no_response_updated",
    );
  };

  const removeContact = (index: number) => {
    emitChange(
      normalizedContacts.filter((_, currentIndex) => currentIndex !== index),
      "removed",
    );
  };

  const handleSendEmail = (contact: ContactManagerItem) => {
    if (onSendEmail) {
      onSendEmail(contact);
      return;
    }
    const email = contact.email?.trim();
    if (!email) {
      notifyMissingInfo({
        title: "Missing email",
        description: "This contact does not have an email address.",
      });
      return;
    }
    window.location.href = `mailto:${email}`;
  };

  const handleSendWhatsApp = (contact: ContactManagerItem) => {
    if (onSendWhatsApp) {
      onSendWhatsApp(contact);
      return;
    }
    const phoneOptions = parsePhoneOptions(contact.telephone);
    if (phoneOptions.length === 0) {
      notifyMissingInfo({
        title: "Missing phone",
        description: "This contact does not have a phone number.",
      });
      return;
    }
    const normalizedPhone = phoneOptions[0].replace(/\D/g, "");
    if (!normalizedPhone) {
      notifyMissingInfo({
        title: "Invalid phone",
        description: "This contact phone number is invalid for WhatsApp.",
      });
      return;
    }
    window.open(`https://wa.me/${normalizedPhone}`, "_blank", "noopener,noreferrer");
  };

  const handleCall = (contact: ContactManagerItem) => {
    if (onSendCall) {
      onSendCall(contact);
      return;
    }
    const phoneOptions = parsePhoneOptions(contact.telephone);
    if (phoneOptions.length === 0) {
      notifyMissingInfo({
        title: "Missing phone",
        description: "This contact does not have a phone number.",
      });
      return;
    }
    window.location.href = `tel:${phoneOptions[0]}`;
  };

  const handleDragStart = (list: "priority" | "other", index: number) => (event: DragEvent<HTMLDivElement>) => {
    event.dataTransfer.setData("text/plain", `${list}:${index}`);
    event.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const resolveDragPayload = (event: DragEvent<HTMLDivElement>) => {
    const raw = event.dataTransfer.getData("text/plain");
    if (!raw) return null;
    const [list, indexRaw] = raw.split(":");
    const parsed = indexRaw ? Number(indexRaw) : NaN;
    if ((list !== "priority" && list !== "other") || Number.isNaN(parsed)) {
      return null;
    }
    return { list: list as "priority" | "other", index: parsed };
  };

  const handleDropToPriority = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const payload = resolveDragPayload(event);
    if (!payload) return;
    if (payload.list === "other") {
      setPriorityContact(payload.index);
    }
  };

  const handleDropToOther = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const payload = resolveDragPayload(event);
    if (!payload) return;
    if (payload.list === "priority") {
      setPriorityContact(null);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold">Contacts</p>
          <p className="text-xs text-muted-foreground">
            {normalizedContacts.length} total · Drag a contact to set priority.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={startAddContact}
          className="shadow-sm hover:shadow"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add contact
        </Button>
      </div>

      {addingContact && (
        <div className="rounded-md border border-border bg-background p-3 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">
              {editingContactIndex === null ? "Add contact" : "Edit contact"}
            </p>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={disabled}
              className="h-7 w-7"
              onClick={resetContactForm}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              value={newContact.name ?? ""}
              onChange={(event) => setNewContact((prev) => ({ ...prev, name: event.target.value }))}
              placeholder="Contact name"
              disabled={disabled}
            />
            <Input
              value={newContact.position ?? ""}
              onChange={(event) => setNewContact((prev) => ({ ...prev, position: event.target.value }))}
              placeholder="Position"
              disabled={disabled}
            />
            <Input
              value={newContact.email ?? ""}
              onChange={(event) => setNewContact((prev) => ({ ...prev, email: event.target.value }))}
              placeholder="Email"
              disabled={disabled}
            />
            <Input
              value={newContact.telephone ?? ""}
              onChange={(event) => setNewContact((prev) => ({ ...prev, telephone: event.target.value }))}
              placeholder="Phone"
              disabled={disabled}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={resetContactForm}
            >
              Cancel
            </Button>
            <Button type="button" size="sm" disabled={disabled} onClick={saveContact}>
              {editingContactIndex === null ? "Save contact" : "Save changes"}
            </Button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <span>Priority contact</span>
        <span>{priorityContact ? "1/1" : "0/1"}</span>
      </div>

      <div className="space-y-3" onDragOver={handleDragOver} onDrop={handleDropToPriority}>
        {!priorityContact ? (
          <div className="rounded-md border border-dashed border-border/70 bg-muted/20 p-4 text-sm text-muted-foreground">
            Drop a contact here to set priority.
          </div>
        ) : (
          <div
            className="rounded-md border border-border p-3 bg-muted/10"
            draggable
            onDragStart={handleDragStart("priority", priorityContactIndex)}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium">{priorityContact.name || "Unnamed contact"}</p>
                  {priorityContact.no_response && (
                    <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 text-[10px]">
                      No response
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{priorityContact.position || ""}</p>
                <div className="mt-2 text-xs text-muted-foreground space-y-1">
                  <p className="flex items-center gap-2">
                    <Mail className="h-3 w-3" />
                    {priorityContact.email || "No email"}
                  </p>
                  <p className="flex items-center gap-2">
                    <Phone className="h-3 w-3" />
                    {priorityContact.telephone || "No phone"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button type="button" variant="ghost" size="icon" disabled={disabled} className="h-9 w-9">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className={contactMenuContentClassName}>
                    <DropdownMenuItem className={contactMenuItemClassName} onClick={() => setPriorityContact(null)}>
                      <StarOff className="h-4 w-4" />
                      <span>Remove priority</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className={contactMenuSeparatorClassName} />
                    <DropdownMenuItem className={contactMenuItemClassName} onClick={() => handleSendEmail(priorityContact)}>
                      <Mail className="h-4 w-4" />
                      <span>Send email</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem className={contactMenuItemClassName} onClick={() => handleSendWhatsApp(priorityContact)}>
                      <MessageCircle className="h-4 w-4" />
                      <span>Send WhatsApp</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem className={contactMenuItemClassName} onClick={() => handleCall(priorityContact)}>
                      <Phone className="h-4 w-4" />
                      <span>Call</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className={contactMenuSeparatorClassName} />
                    <DropdownMenuItem
                      className={contactMenuItemClassName}
                      onClick={() => updateContactNoResponse(priorityContactIndex, !priorityContact.no_response)}
                    >
                      <X className="h-4 w-4" />
                      <span>{priorityContact.no_response ? "Clear no response" : "Mark no response"}</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className={contactMenuSeparatorClassName} />
                    <DropdownMenuItem className={contactMenuItemClassName} onClick={() => startEditContact(priorityContactIndex)}>
                      <Pencil className="h-4 w-4" />
                      <span>Edit contact</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className={`${contactMenuItemClassName} text-destructive focus:text-destructive`}
                      onClick={() => removeContact(priorityContactIndex)}
                    >
                      <Trash2 className="h-4 w-4" />
                      <span>Delete contact</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="h-px bg-border/70" />

      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold">Other contacts</p>
          <p className="text-xs text-muted-foreground">
            {otherContacts.length} total
            {otherContacts.length > 5 ? ` · Showing ${visibleOtherContacts.length}` : ""}
          </p>
        </div>
      </div>
      {otherContacts.length === 0 ? (
        <p className="text-sm text-muted-foreground">No other contacts.</p>
      ) : (
        <div className="space-y-3 max-h-[360px] overflow-y-auto pr-1" onDragOver={handleDragOver} onDrop={handleDropToOther}>
          {visibleOtherContacts.map((entry) => (
            <div
              key={`contact-${entry.index}`}
              className="rounded-md border border-border p-3 bg-muted/10"
              draggable
              onDragStart={handleDragStart("other", entry.index)}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">{entry.contact.name || "Unnamed contact"}</p>
                    {entry.contact.no_response && (
                      <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 text-[10px]">
                        No response
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{entry.contact.position || ""}</p>
                  <div className="mt-2 text-xs text-muted-foreground space-y-1">
                    <p className="flex items-center gap-2">
                      <Mail className="h-3 w-3" />
                      {entry.contact.email || "No email"}
                    </p>
                    <p className="flex items-center gap-2">
                      <Phone className="h-3 w-3" />
                      {entry.contact.telephone || "No phone"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button type="button" variant="ghost" size="icon" disabled={disabled} className="h-9 w-9">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className={contactMenuContentClassName}>
                      <DropdownMenuItem className={contactMenuItemClassName} onClick={() => setPriorityContact(entry.index)}>
                        <Star className="h-4 w-4" />
                        <span>Set priority</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className={contactMenuSeparatorClassName} />
                      <DropdownMenuItem className={contactMenuItemClassName} onClick={() => handleSendEmail(entry.contact)}>
                        <Mail className="h-4 w-4" />
                        <span>Send email</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem className={contactMenuItemClassName} onClick={() => handleSendWhatsApp(entry.contact)}>
                        <MessageCircle className="h-4 w-4" />
                        <span>Send WhatsApp</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem className={contactMenuItemClassName} onClick={() => handleCall(entry.contact)}>
                        <Phone className="h-4 w-4" />
                        <span>Call</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className={contactMenuSeparatorClassName} />
                      <DropdownMenuItem
                        className={contactMenuItemClassName}
                        onClick={() => updateContactNoResponse(entry.index, !entry.contact.no_response)}
                      >
                        <X className="h-4 w-4" />
                        <span>{entry.contact.no_response ? "Clear no response" : "Mark no response"}</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className={contactMenuSeparatorClassName} />
                      <DropdownMenuItem className={contactMenuItemClassName} onClick={() => startEditContact(entry.index)}>
                        <Pencil className="h-4 w-4" />
                        <span>Edit contact</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className={`${contactMenuItemClassName} text-destructive focus:text-destructive`}
                        onClick={() => removeContact(entry.index)}
                      >
                        <Trash2 className="h-4 w-4" />
                        <span>Delete contact</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
          ))}
          {otherContacts.length > 5 && (
            <Button type="button" variant="ghost" size="sm" onClick={() => setShowAllContacts((prev) => !prev)} className="w-full">
              {showAllContacts ? "Show less contacts" : `Show all contacts (${otherContacts.length})`}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactManagerPanel;
